import { VaultOwnershipFiltersIds } from 'common/enums';
import { publishStorageFreed } from 'helpers/upload-progress-bus';
import { ApiService } from 'networking/api-service';
import { API_ROUTES } from 'networking/api-routes';
import { VaultsSerializer } from 'networking/serializers/vaults-serializer';
import { VaultsPagination } from 'models/vaultsPagination';
import { Clip } from 'models/clip';
import { Vault } from 'models/vault';
import { TemplateAndQuestionsSerializer } from 'networking/serializers/templates-and-questions-serializer';
import { TemplateAndQuestions } from 'models/template-and-questions';
import { ContributorSerializer } from 'networking/serializers/contributor-serializer';
import { Contributor } from 'models/contributor';
import { ContributorInvitation } from 'models/contributor-invitation';
import { VaultsQueryParamsSerialized } from 'networking/types/vaults-query-params';
import { RecordingGuest } from 'models/recording-guest';
import { PaymentSerializer } from 'networking/serializers/payment-serializer';
import { AlbumContentRaw, AlbumDetailRaw } from 'networking/types/album-detail';
import { SharedAlbum, SharedAlbumRaw } from 'networking/types/shared-album';
import { DocumentRaw } from 'networking/types/documents';
import { AlbumLocationRaw } from 'networking/types/locations';

class VaultsController {
  static async getVaults(queryParams: VaultsQueryParamsSerialized) {
    const params = VaultsSerializer.serializeVaultsQueryParams(queryParams);

    const response = await ApiService.get<VaultsPaginationRaw>(
      API_ROUTES.VAULTS,
      params,
    );

    const deSerializedVaults = VaultsSerializer.deSerializeVaults(
      response.data,
    );

    return new VaultsPagination(deSerializedVaults);
  }

  // The vaults whose settings the person may open, by title: the ones they
  // own and the ones they edit (role=contributor lists editors, the old
  // "contributors"). Viewers and contributors manage none.
  static async getManagedVaults(pageSize: number): Promise<VaultType[]> {
    const roles = [
      VaultOwnershipFiltersIds.owner,
      VaultOwnershipFiltersIds.contributor,
    ];
    const lists = await Promise.all(
      roles.map((role) =>
        VaultsController.getVaults({
          pageNum: 1,
          pageSize,
          sortBy: 'title:asc',
          role,
        }),
      ),
    );
    return lists
      .flatMap(({ data }) => data)
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  // `withAlbums`: true returns every album with all its memories (the vault
  // dashboard needs that); 'summary' returns the albums without their
  // contents, a fraction of the payload for screens that only need the vault
  // and its album list (album page, settings, import, the viewer).
  static async getVault(id: number, withAlbums?: boolean | 'summary') {
    // `with_albums` (renamed backend) and `with_memories` (legacy/QA backend)
    // request the nested collection; send both so either backend includes it.
    let params: Record<string, string> | undefined;
    if (withAlbums === 'summary') params = { with_albums: 'summary' };
    else if (withAlbums) params = { with_albums: 'yes', with_memories: 'yes' };
    const response = await ApiService.get<VaultRaw>(
      API_ROUTES.VAULT_DETAIL(id),
      params,
    );

    const deSerializedVaults = VaultsSerializer.deSerializeVault(response.data);

    return new Vault(deSerializedVaults);
  }

  // The storage-based vault plans (25GB..1TB, temporary/long-term).
  static async getStoragePlans(): Promise<StoragePlanType[]> {
    const response = await ApiService.get<{ storage_plans: StoragePlanRaw[] }>(
      API_ROUTES.STORAGE_PLANS,
    );

    return (response.data.storage_plans ?? []).map(
      VaultsSerializer.deSerializeStoragePlan,
    );
  }

  // Albums (with memories) other people have shared with the current user, each
  // annotated with the sharer (`sharedBy`) and granted role.
  static async getSharedWithMe(): Promise<SharedAlbum[]> {
    const response = await ApiService.get<{ albums: SharedAlbumRaw[] }>(
      API_ROUTES.SHARED_WITH_ME,
    );

    return (response.data.albums ?? []).map(
      VaultsSerializer.deSerializeSharedAlbum,
    );
  }

  static async deleteClip(id: number) {
    const clipDeleted = await ApiService.delete<ClipRaw>(
      API_ROUTES.DELETE_CLIP(id),
    );

    const data = VaultsSerializer.deSerializeClip(clipDeleted.data);

    return new Clip(data);
  }

  static async deleteVault(VaultId: number) {
    await ApiService.delete<void>(API_ROUTES.DELETE_VAULT(VaultId));
  }

  static async createVault(newVault: VaultNewRequestType) {
    const serializedNewVault = VaultsSerializer.serializeNewVault(newVault);

    const allData = await ApiService.post<VaultRaw>(
      API_ROUTES.VAULTS,
      serializedNewVault,
    );
    const data = VaultsSerializer.deSerializeVault(allData.data);

    return new Vault(data);
  }

  // Opens a hosted Stripe Checkout session for a vault awaiting its first
  // payment. Returns the URL the browser should be sent to.
  static async createCheckoutSession(vaultId: number): Promise<string> {
    const response = await ApiService.post<{ checkout_url: string }>(
      API_ROUTES.VAULT_CHECKOUT_SESSION(vaultId),
      {},
    );

    return response.data.checkout_url;
  }

  static async editVaultQuestions(vaultId: number, data: VaultEditRequestType) {
    const serializedNewVault = VaultsSerializer.serializeVaultEditRequest(data);

    await ApiService.put<VaultEditRequestRaw>(
      API_ROUTES.VAULT_EDIT(vaultId),
      serializedNewVault,
    );
  }

  static async editVaultModal(
    vaultId: number,
    requestData: VaultEditRequestType,
  ) {
    const serializedVaultEditRequest =
      VaultsSerializer.serializeVaultEditRequest(requestData);

    await ApiService.put<void>(
      API_ROUTES.VAULT_EDIT(vaultId),
      serializedVaultEditRequest,
    );
  }

  static async getDefaultQuestions() {
    const response = await ApiService.get<DefaultQuestionsRaw>(
      API_ROUTES.GET_DEFAULT_QUESTIONS,
    );

    const deserializedTemplatesAndQuestions =
      TemplateAndQuestionsSerializer.deserializeDefaultQuestions(response.data);

    return new TemplateAndQuestions(deserializedTemplatesAndQuestions);
  }

  static async getSharedVault(vaultId: number | string) {
    const response = await ApiService.get<VaultRaw>(
      API_ROUTES.VAULT_DETAIL_SHARED(vaultId),
    );

    const deSerializedVaults = VaultsSerializer.deSerializeVault(response.data);

    return new Vault(deSerializedVaults);
  }

  static async getVideoCallVault(vaultId: number, videoCallToken: string) {
    const response = await ApiService.get<VaultRaw>(
      API_ROUTES.VAULT_DETAIL_VIDEO_CALL(vaultId, videoCallToken),
    );

    const deSerializedVaults = VaultsSerializer.deSerializeVault(response.data);

    return new Vault(deSerializedVaults);
  }

  static async editClip(idClip: number, requestData: ClipEditRequestType) {
    const serializedClipEditRequest =
      VaultsSerializer.serializeClipEditRequest(requestData);

    const response = await ApiService.put<ClipRaw>(
      API_ROUTES.EDIT_CLIP(idClip),
      serializedClipEditRequest,
    );

    const deSerializedClip = VaultsSerializer.deSerializeClip(response.data);

    return new Clip(deSerializedClip);
  }

  static async shareClipByEmail({
    clipId,
    email,
  }: {
    clipId: number;
    email: string;
  }) {
    await ApiService.post(API_ROUTES.SHARE_CLIP_BY_EMAIL(clipId), { email });
  }

  static async destroyExtraClips(
    vaultId: number,
    data: DestroyClipsRequestType,
  ) {
    const serializedDestroyClipsRequest =
      VaultsSerializer.serializeDestroyClipsRequest(data);

    await ApiService.put(
      API_ROUTES.DESTROY_CLIPS(vaultId),
      serializedDestroyClipsRequest,
    );
  }

  static async sendLinkToVideoCall({ id }: { id: number }) {
    await ApiService.post(API_ROUTES.SEND_LINK_TO_VIDEO_CALL(id));
  }

  static async requestVaultDownload({
    id,
    includePrivateClips,
  }: {
    id: number;
    includePrivateClips: boolean;
  }) {
    return ApiService.post(API_ROUTES.REQUEST_VAULT_DOWNLOAD(id), {
      include_private_clips: includePrivateClips,
    });
  }

  static async requestAlbumDownload({
    id,
    includePrivateClips,
  }: {
    id: number;
    includePrivateClips: boolean;
  }) {
    return ApiService.post(API_ROUTES.REQUEST_ALBUM_DOWNLOAD(id), {
      include_private_clips: includePrivateClips,
    });
  }

  static async createClip(vaultId: number, data: CreateClipRequestType) {
    const serializedCreateClipRequest =
      VaultsSerializer.serializeCreateClipRequest(data);

    const response = await ApiService.post<ClipRaw>(
      API_ROUTES.CREATE_CLIP(vaultId),
      serializedCreateClipRequest,
    );

    const deSerializedClip = VaultsSerializer.deSerializeClip(response.data);

    return deSerializedClip;
  }

  static async createDocument(vaultId: number, data: CreateClipRequestType) {
    const serializedCreateDocumentRequest =
      VaultsSerializer.serializeCreateDocumentRequest(data, vaultId);

    const response = await ApiService.post<DocumentRaw>(
      API_ROUTES.CREATE_DOCUMENT(vaultId),
      serializedCreateDocumentRequest,
    );

    const deSerializedDocument = VaultsSerializer.deSerializeDocument(
      response.data,
    );

    return deSerializedDocument;
  }

  static async createDocumentWithToken(
    albumId: number,
    vaultId: number,
    data: CreateClipRequestType,
  ) {
    const serializedCreateDocumentRequest =
      VaultsSerializer.serializeCreateDocumentRequest(data, vaultId);

    const response = await ApiService.post<{ document: DocumentRaw }>(
      API_ROUTES.CREATE_ALBUM_DOCUMENT_WITH_TOKEN(albumId),
      serializedCreateDocumentRequest,
    );

    const deSerializedDocument = VaultsSerializer.deSerializeDocument(
      response.data.document,
    );

    return deSerializedDocument;
  }

  static async modifyVaultSubscription(
    vaultId: number,
    tier?: SubscriptionSchemeId,
  ) {
    const response = await ApiService.put<VaultRaw>(
      API_ROUTES.UPGRADE_VAULT(vaultId),
      { tier },
    );

    const deSerializedVaults = VaultsSerializer.deSerializeVault(response.data);

    return new Vault(deSerializedVaults);
  }

  static async cancelSubscription(vaultId: number) {
    await ApiService.post(API_ROUTES.CANCEL_SUBSCRIPTION(vaultId));
  }

  static async addContributors(
    vaultId: number,
    contributors: {
      name: string;
      email: string;
    }[],
    note?: string,
  ) {
    const response = await ApiService.post<ContributorInvitationRaw>(
      API_ROUTES.ADD_CONTRIBUTOR(vaultId),
      { contributors, note },
    );

    const deSerializedInvitation = ContributorSerializer.deSerializeInvitation(
      response.data,
    );

    return new ContributorInvitation(deSerializedInvitation);
  }

  static async removeContributor({
    vaultId,
    invitationId,
  }: {
    vaultId: number;
    invitationId: number;
  }) {
    await ApiService.delete<void>(
      API_ROUTES.REMOVE_CONTRIBUTOR(vaultId, invitationId),
    );
  }

  static async removeGuest({ guestId }: { guestId: number }) {
    await ApiService.delete<void>(API_ROUTES.REMOVE_GUEST(guestId));
  }

  static async leaveVault(vaultId: number) {
    await ApiService.delete<void>(API_ROUTES.LEAVE_VAULT(vaultId));
  }

  static async resendVaultInvitation(invitationId: number) {
    const response = await ApiService.put<ContributorRaw>(
      API_ROUTES.RESEND_VAULT_INVITATION(invitationId),
    );

    const deSerializedContributor = VaultsSerializer.deSerializeContributorInfo(
      response.data,
    );

    return new Contributor(deSerializedContributor);
  }

  static async inviteGuestsToRecord({
    vaultId,
    guests,
    expiration,
    note,
  }: InviteGuestRequestType) {
    const response = await ApiService.post<RecordingGuestType>(
      API_ROUTES.INVITE_GUEST_TO_RECORD(vaultId),
      { guests, expiration_date: expiration, note },
    );

    const deSerializedGuest = VaultsSerializer.deSerializeGuest(response.data);

    return new RecordingGuest(deSerializedGuest);
  }

  static async getVaultForGuest(id: number, withAlbums?: boolean) {
    const params = withAlbums ? { with_albums: 'yes' } : undefined;
    const response = await ApiService.get<VaultRaw>(
      API_ROUTES.VAULT_GUEST(id),
      params,
    );

    const deSerializedVaults = VaultsSerializer.deSerializeVault(response.data);

    return new Vault(deSerializedVaults);
  }

  static async guestSelfRegister({
    guestInviteToken,
    email,
    name,
  }: {
    guestInviteToken: string;
    email: string;
    name: string;
  }) {
    const response = await ApiService.post<GuestRaw>(
      API_ROUTES.GUEST_SELF_REGISTER,
      {
        guest_invite_token: guestInviteToken,
        email,
        name,
      },
    );

    const deSerializedGuest = VaultsSerializer.deSerializeGuestInfo(
      response.data,
    );

    return deSerializedGuest;
  }

  static async albumGuestSelfRegister({
    guestInviteToken,
    email,
    name,
  }: {
    guestInviteToken: string;
    email: string;
    name: string;
  }) {
    const response = await ApiService.post<GuestRaw>(
      API_ROUTES.ALBUM_GUEST_SELF_REGISTER,
      {
        guest_invite_token: guestInviteToken,
        email,
        name,
      },
    );

    const deSerializedGuest = VaultsSerializer.deSerializeGuestInfo(
      response.data,
    );

    return deSerializedGuest;
  }

  static async createClipByGuest(vaultId: number, data: CreateClipRequestType) {
    const serializedCreateClipRequest =
      VaultsSerializer.serializeCreateClipRequest(data);

    const response = await ApiService.post<ClipRaw>(
      API_ROUTES.CREATE_CLIP_GUEST(vaultId),
      serializedCreateClipRequest,
    );

    const deSerializedClip = VaultsSerializer.deSerializeClip(response.data);

    return deSerializedClip;
  }

  static async editClipByGuest(
    idClip: number,
    requestData: ClipEditRequestType,
  ) {
    const serializedClipEditRequest =
      VaultsSerializer.serializeClipEditRequest(requestData);

    const response = await ApiService.put<ClipRaw>(
      API_ROUTES.EDIT_CLIP_GUEST(idClip),
      serializedClipEditRequest,
    );

    const deSerializedClip = VaultsSerializer.deSerializeClip(response.data);

    return new Clip(deSerializedClip);
  }

  static async createReminder(vaultId: number, data: ReminderRequestType) {
    const serializedReminderRequest =
      VaultsSerializer.serializeReminderRequest(data);

    const response = await ApiService.post<ReminderResponseType>(
      API_ROUTES.CREATE_REMINDER(vaultId),
      serializedReminderRequest,
    );
    const deSerializedReminder = VaultsSerializer.deSerializeReminder(
      response.data,
    );

    return deSerializedReminder;
  }

  static async deleteReminder(vaultId: number, reminderId: number) {
    await ApiService.delete(API_ROUTES.DELETE_REMINDER(vaultId, reminderId));
  }

  static async editReminder(
    vaultId: number,
    reminderId: number,
    data: ReminderRequestType,
  ) {
    const serializedReminderRequest =
      VaultsSerializer.serializeReminderRequest(data);

    const response = await ApiService.put<ReminderResponseType>(
      API_ROUTES.EDIT_REMINDER(vaultId, reminderId),
      serializedReminderRequest,
    );
    const deSerializedReminder = VaultsSerializer.deSerializeReminder(
      response.data,
    );

    return deSerializedReminder;
  }

  static async sendSampleEmail(vaultId: number) {
    await ApiService.post(API_ROUTES.SEND_SAMPLE_EMAIL(vaultId));
  }

  static async updateVaultQuestions(
    vaultId: number,
    data: VaultEditRequestType,
  ) {
    const serializedNewVault = VaultsSerializer.serializeVaultEditRequest(data);

    const response = await ApiService.put<VaultRaw>(
      API_ROUTES.VAULT_EDIT(vaultId),
      serializedNewVault,
    );
    const deSerializedVaults = VaultsSerializer.deSerializeVault(response.data);

    return deSerializedVaults;
  }

  static async buyClips(vaultId: number, data: BuyClipsRaw) {
    const response = await ApiService.post<PaymentInfoRaw>(
      API_ROUTES.BUY_CLIPS(vaultId),
      data,
    );

    const deSerializedPayment = PaymentSerializer.deSerializePaymentInfo(
      response.data,
    );

    return deSerializedPayment;
  }

  static async buyExtraCallTime(vaultId: number, extraCallTime: number) {
    const serializedBuyExtraCallTime =
      VaultsSerializer.serializeBuyExtraCallTime(extraCallTime);
    const response = await ApiService.post<PaymentInfoRaw>(
      API_ROUTES.BUY_EXTRA_CALL_TIME(vaultId),
      serializedBuyExtraCallTime,
    );
    const deSerializedPaymentInfo = PaymentSerializer.deSerializePaymentInfo(
      response.data,
    );
    return deSerializedPaymentInfo;
  }

  static async getPayment(paymentId: number) {
    const response = await ApiService.get<PaymentInfoRaw>(
      API_ROUTES.GET_PAYMENT(paymentId),
    );

    const deSerializedPayment = PaymentSerializer.deSerializePaymentInfo(
      response.data,
    );

    return deSerializedPayment;
  }

  static async getVaultsCollaborators(
    queryParams: CollaboratorsQueryParamsSerialized,
  ) {
    const params =
      VaultsSerializer.serializeCollaboratorsQueryParams(queryParams);

    const response = await ApiService.get<VaultsPaginationRaw>(
      API_ROUTES.GET_COLLABORATORS,
      params,
    );

    const deSerializedVaults = VaultsSerializer.deSerializeVaults(
      response.data,
    );

    return new VaultsPagination(deSerializedVaults);
  }

  static async exportVaultCollaborators(
    queryParams: CollaboratorsQueryParamsSerialized,
  ) {
    const params =
      VaultsSerializer.serializeCollaboratorsQueryParams(queryParams);

    const response = await ApiService.get<Blob>(
      API_ROUTES.GET_COLLABORATORS,
      params,
    );

    const blob = new Blob([response.data]);

    const contentDisposition: string = response.headers['content-disposition'];
    let filename = 'collaborators.csv';

    if (contentDisposition) {
      const match = contentDisposition.match(/filename=(.+)/);
      if (match) {
        [, filename] = match;
      }
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return response.data;
  }

  static async getOwnedInvites() {
    const response = await ApiService.get<OwnedInvitesRaw>(
      API_ROUTES.GET_OWNED_INVITES,
    );

    const ownedInvites = VaultsSerializer.deSerializeOwnedInvites(
      response.data,
    );

    return ownedInvites;
  }

  static async resendPendingInvites(
    queryParams: ResendPendingInvitesQueryParams,
  ) {
    const response = await ApiService.get<ProcessedInvitesRaw>(
      API_ROUTES.RESEND_PENDING_INVITES,
      queryParams,
    );
    return response.data.processed_collaborators.length;
  }

  static async getVaultForInvite(id: number, token: string) {
    const response = await ApiService.get<VaultRaw>(
      API_ROUTES.VAULT_INFO_ONBOARDING(id, token),
    );

    const deSerializedVaults = VaultsSerializer.deSerializeVault(response.data);

    return new Vault(deSerializedVaults);
  }

  static async getCollaboratorAuditDetail(
    queryParams: CollaboratorAuditDetailQueryParams,
  ) {
    const params =
      VaultsSerializer.serializeAuditDetailQueryParams(queryParams);
    const response = await ApiService.get<CollaboratorAuditDetailRaw>(
      API_ROUTES.GET_COLLABORATOR_AUDIT_DETAIL,
      params,
    );

    const deSerializedCollaborators =
      VaultsSerializer.deserializeCollaboratorAuditDetail(response.data);

    return deSerializedCollaborators;
  }

  // New albums are dated today unless a range is given (the importer passes
  // the span of the album's memories).
  static async createAlbum(
    vaultId: number,
    name: string = 'This is the Album Title',
    dates?: { startDate?: string | null; endDate?: string | null },
  ) {
    const response = await ApiService.post<AlbumDetailRaw>(
      API_ROUTES.CREATE_ALBUM(vaultId),
      { album: { name } },
    );

    const album = VaultsSerializer.deSerializeAlbumDetail(response.data);
    const startDate = dates?.startDate || new Date().toISOString();
    const endDate = dates?.endDate || startDate;
    try {
      await this.updateAlbumMetadata(album.id, { startDate, endDate });

      return { ...album, startDate, endDate };
    } catch (error) {
      return album;
    }
  }

  static async getAlbum(id: number) {
    const response = await ApiService.get<AlbumDetailRaw>(
      API_ROUTES.ALBUM_DETAIL(id),
    );
    return VaultsSerializer.deSerializeAlbumDetail(response.data);
  }

  static async getSharedAlbum(albumId: number | string) {
    const response = await ApiService.get<AlbumDetailRaw>(
      API_ROUTES.ALBUM_DETAIL_SHARED(albumId),
    );

    return VaultsSerializer.deSerializeAlbumDetail(response.data);
  }

  static async getSharedContent(contentId: number | string) {
    const response = await ApiService.get<AlbumContentRaw>(
      API_ROUTES.CONTENT_DETAIL_SHARED(contentId),
    );

    return VaultsSerializer.albumDetailContentDeserializer(response.data);
  }

  static async updateAlbum(id: number, isPrivate: boolean) {
    const response = await ApiService.patch<AlbumDetailRaw>(
      API_ROUTES.UPDATE_ALBUM(id),
      {
        album: {
          is_private: isPrivate,
        },
      },
    );
    return VaultsSerializer.deSerializeAlbumDetail(response.data);
  }

  // `freedBytes` (when known) drives the storage indicator's release
  // animation after a successful delete.
  static async deleteAlbum(id: number, freedBytes?: number) {
    await ApiService.delete<void>(API_ROUTES.ALBUM_DETAIL(id));
    if (freedBytes) publishStorageFreed(freedBytes);
  }

  static async deleteAlbumContent(id: number, freedBytes?: number) {
    await ApiService.delete<void>(API_ROUTES.CONTENT_DETAIL(id));
    if (freedBytes) publishStorageFreed(freedBytes);
  }

  static async updateAlbumMetadata(id: number, data: AlbumMetadataRequestType) {
    const serializedAlbumMetadataRequest =
      VaultsSerializer.serializeAlbumMetadataRequest(id, data);
    await ApiService.put<void>(
      API_ROUTES.EDIT_ALBUM_METADATA(id),
      serializedAlbumMetadataRequest,
    );
  }

  static async addJournalEntry(
    albumId: number | null,
    data: JournalEntryRequestType,
    vaultId?: number,
  ) {
    const serializedJournalEntryRequest =
      VaultsSerializer.serializeJournalEntryRequest(albumId, data, vaultId);
    const response = await ApiService.post<JournalDocumentRaw>(
      API_ROUTES.ADD_JOURNAL_ENTRY,
      serializedJournalEntryRequest,
    );
    return VaultsSerializer.deSerializeJournalDocument(response.data);
  }

  static async updateJournalEntry(
    journalDocumentId: number,
    data: UpdateJournalEntryRequestType,
  ) {
    const serializedRequest =
      VaultsSerializer.serializeUpdateJournalEntryRequest(data);
    const response = await ApiService.put<JournalDocumentRaw>(
      API_ROUTES.UPDATE_JOURNAL_ENTRY(journalDocumentId),
      serializedRequest,
    );
    return VaultsSerializer.deSerializeJournalDocument(response.data);
  }

  static async updateContentMetadata(
    id: number,
    data: ContentMetadataRequestType,
  ) {
    const serializedContentMetadataRequest =
      VaultsSerializer.serializeContentMetadataRequest(id, data);
    await ApiService.put<void>(
      API_ROUTES.CONTENT_DETAIL(id),
      serializedContentMetadataRequest,
    );
  }

  static async getAlbumLocations(vaultId: number) {
    const response = await ApiService.get<AlbumLocationRaw>(
      API_ROUTES.GET_ALBUM_LOCATIONS(vaultId),
    );
    return VaultsSerializer.deSerializeAlbumLocations(response.data);
  }

  static async updateContentOrder(data: ContentOrderRequestType) {
    const serializedContentOrderRequest =
      VaultsSerializer.serializeContentOrderRequest(data);
    await ApiService.put<void>(
      API_ROUTES.UPDATE_CONTENT_ORDER,
      serializedContentOrderRequest,
    );
  }

  // Saves the vault's own order for all of its memories ("All memories").
  static async updateVaultMemoryOrder(vaultId: number, contentIds: number[]) {
    await ApiService.put<void>(API_ROUTES.UPDATE_VAULT_MEMORY_ORDER(vaultId), {
      content_ids: contentIds,
    });
  }

  static async updateAlbumOrder(data: AlbumOrderRequestType) {
    const serializedAlbumOrderRequest =
      VaultsSerializer.serializeAlbumOrderRequest(data);
    await ApiService.put<void>(
      API_ROUTES.UPDATE_ALBUM_ORDER,
      serializedAlbumOrderRequest,
    );
  }
}

export { VaultsController };
